/**
 * Live impact stats band — rAF count-ups driven by the public /stats API, revealed on
 * scroll. Numbers animate once in view; prefers-reduced-motion jumps to the final value.
 */
import { useEffect, useState } from 'react';
import { Users, IndianRupee, MapPin, CalendarCheck } from 'lucide-react';
import { useReveal } from '../hooks/useReveal.js';
import { useCountUp } from '../hooks/useCountUp.js';
import { publicApi } from '../lib/publicApi.js';
import styles from './StatsBand.module.css';

export function StatsBand() {
  const [stats, setStats] = useState(null);
  const ref = useReveal({ threshold: 0.3 });
  const [active, setActive] = useState(false);

  useEffect(() => {
    publicApi.stats().then(setStats).catch(() => setStats({ families: 0, deployed: 0, villages: 0, casesThisMonth: 0 }));
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const obs = new IntersectionObserver((e) => e[0].isIntersecting && setActive(true), { threshold: 0.3 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [ref]);

  return (
    <section id="impact" className={styles.band} ref={ref}>
      <div className="container">
        <div className={styles.inner}>
          <Stat icon={Users} value={stats?.families ?? 0} active={active} label="Families served" />
          <Stat icon={IndianRupee} value={stats?.deployed ?? 0} active={active} label="Rupees deployed" money />
          <Stat icon={MapPin} value={stats?.villages ?? 0} active={active} label="Villages reached" />
          <Stat icon={CalendarCheck} value={stats?.casesThisMonth ?? 0} active={active} label="Cases this month" />
        </div>
        <p className={styles.liveNote}>
          <span className={styles.dot} /> Updated live from the case database
        </p>
      </div>
    </section>
  );
}

function Stat({ icon: Icon, value, label, active, money }) {
  const n = useCountUp(value, { active });
  const display = money ? `₹${n.toLocaleString('en-IN')}` : n.toLocaleString('en-IN');
  return (
    <div className={styles.stat}>
      <Icon className={styles.icon} size={22} aria-hidden="true" />
      <span className={`${styles.value} mono`}>{display}</span>
      <span className={styles.label}>{label}</span>
    </div>
  );
}

export default StatsBand;
